// Offline buffer for the web driver's GPS fixes — the same idea as the mobile
// app's trackingQueue: while the connection is down, sends fail but fixes keep
// coming; we hold them here (with their capture time) and drain the backlog via
// the batch endpoint once connectivity returns, so the manager's track has no
// gaps. In-memory is enough: the web driver keeps the tab open while driving,
// and a closed tab stops producing fixes anyway.

export type BufferedTrackPoint = {
  latitude: number
  longitude: number
  accuracy?: number
  ts: number // epoch milliseconds (capture time)
}

// Bound memory if connectivity is lost for a very long time: keep the most
// recent points and drop the oldest once over the cap.
const MAX_QUEUE = 1000
// Upload at most this many points per request so a large backlog drains in
// bounded chunks instead of one huge payload (backend caps a batch at 500).
const FLUSH_CHUNK = 200

// Same rule as the server: out-of-range coordinates and the (0,0) "no fix yet" point are refused,
// and since the server refuses the whole batch, one such point would block everything behind it.
export function isValidTrackPoint(p: Pick<BufferedTrackPoint, 'latitude' | 'longitude'>): boolean {
  const { latitude: lat, longitude: lon } = p
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false
  return !(lat === 0 && lon === 0)
}

// 422 means the server will never take this batch as it is — retrying it forever would stall the
// queue, so the chunk is dropped. Checked by shape: importing ApiError would pull the API client
// (and Keycloak) into this module.
function isRefusedBatch(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { status?: unknown }).status === 422
}

export function createTrackQueue() {
  let queue: BufferedTrackPoint[] = []
  let flushing = false

  return {
    enqueue(point: BufferedTrackPoint): void {
      if (!isValidTrackPoint(point)) return
      queue.push(point)
      if (queue.length > MAX_QUEUE) {
        queue = queue.slice(queue.length - MAX_QUEUE)
      }
    },

    pending(): number {
      return queue.length
    },

    clear(): void {
      queue = []
    },

    /**
     * Drains the queue in chunks via `send`. Points are removed only after the
     * send for that chunk resolves, so a failure (thrown by `send`) leaves them
     * buffered for the next attempt — except a 422, which the server will never accept
     * (see isRefusedBatch). Unlike the mobile background task, browser
     * timers are not serialized — the `flushing` latch prevents two overlapping
     * flushes from double-sending the same chunk.
     */
    async flush(send: (batch: BufferedTrackPoint[]) => Promise<void>): Promise<void> {
      if (flushing) return
      flushing = true
      try {
        while (queue.length > 0) {
          const batch = queue.slice(0, FLUSH_CHUNK)
          try {
            await send(batch)
          } catch (err) {
            if (!isRefusedBatch(err)) throw err
          }
          queue.splice(0, batch.length)
        }
      } finally {
        flushing = false
      }
    },
  }
}

export type TrackQueue = ReturnType<typeof createTrackQueue>
